import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome,
  FiDollarSign,
  FiCalendar,
  FiMapPin,
  FiUser,
  FiPhone,
  FiMail,
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiEye,
  FiX,
  FiRefreshCw,
  FiBox,
  FiGrid,
  FiList
} from 'react-icons/fi';
import {
  FaHome,
  FaRulerCombined,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaBuilding as FaBuildingIcon,
  FaGem
} from 'react-icons/fa';
import { sendApiRequest } from '../../utils.js';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

const Properties = () => {
  const userData = useSelector((state) => state.user_info[0]?.user);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getAllPropertiesofCurrentUser = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest('api/packages/all_manual/receipts', {}, 'GET');
      if (response.data.success) {
        const filteredReceipts = response.data.data.receipts
          .filter(receipt => receipt.customer.customerEmail === userData?.customerEmail && receipt.isCommissionReceipt === false)
          .map(receipt => ({
            _id: receipt._id,
            title: receipt.package.title,
            area: `${receipt.package.width * receipt.package.length}`,
            areaUnit: 'sq.ft',
            value: receipt.amount,
            location: receipt.remarks || 'Not specified',
            startDate: receipt.receiptDate,
            status: receipt.generatedManually ? 'Active' : 'Pending',
            receiptNumber: receipt.receiptNumber,
            customerName: receipt.customer.customerName,
            customerEmail: receipt.customer.customerEmail,
            customerPhone: receipt.customer.customerPhone,
            partnerName: receipt.partner.fullName,
            partnerEmail: receipt.partner.email,
            partnerPhone: receipt.partner.phone,
            paymentMode: receipt.paymentMode,
            paymentDetails: receipt.paymentDetails,
            amountInWords: receipt.amountInWords,
            createdAt: receipt.createdAt,
            updatedAt: receipt.updatedAt
          }));
        setProperties(filteredReceipts);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to load properties',
        text: 'Please try again later',
        confirmButtonColor: '#6366f1'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllPropertiesofCurrentUser();
  }, []);

  const stats = {
    total: properties.length,
    active: properties.filter(p => p.status === 'Active').length,
    totalValue: properties.reduce((sum, p) => sum + (p.value || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FaGem className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading your properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  My Properties
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Manage and view all your properties</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={getAllPropertiesofCurrentUser}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
              <div className="flex gap-1 bg-white rounded-xl shadow-md p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition ${
                    viewMode === 'grid' 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md' 
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <FiGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition ${
                    viewMode === 'list' 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md' 
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <FiList className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Properties</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">All properties</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FiHome className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Properties</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.active}</p>
                <p className="text-xs text-gray-400 mt-1">Currently active</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FiCheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Value</p>
                <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalValue)}</p>
                <p className="text-xs text-gray-400 mt-1">Portfolio value</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <FiDollarSign className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Properties Grid/List View */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {properties.length > 0 ? (
              properties.map((property, index) => (
                <motion.div
                  key={property._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-4 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md">
                          <FaHome className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{property.title}</h3>
                          <p className="text-xs text-gray-500">Receipt #{property.receiptNumber}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        property.status === 'Active' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {property.status}
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-400">Area</p>
                        <p className="font-semibold text-gray-700">{property.area} {property.areaUnit}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Value</p>
                        <p className="font-semibold text-gray-700">{formatCurrency(property.value)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Location</p>
                        <p className="text-sm text-gray-600 truncate">{property.location}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Receipt Date</p>
                        <p className="text-sm text-gray-600">{formatDate(property.startDate)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedProperty(property)}
                      className="w-full mt-2 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition flex items-center justify-center gap-2 text-sm"
                    >
                      <FiEye className="w-4 h-4" />
                      View Details
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-16 bg-white rounded-2xl shadow-lg">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiHome className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium text-lg">No properties found</p>
                  <p className="text-sm text-gray-400 mt-1">Your properties will appear here</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Property</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Area</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Value</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Receipt Date</th>
                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {properties.length > 0 ? (
                    properties.map((property, index) => (
                      <motion.tr
                        key={property._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setSelectedProperty(property)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                              <FiHome className="w-4 h-4 text-indigo-500" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{property.title}</p>
                              <p className="text-xs text-gray-500">#{property.receiptNumber}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-gray-600">
                            <FiDollarSign className="w-3 h-3 text-gray-400" />
                            <span className="text-sm">{property.area} {property.areaUnit}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-indigo-600">{formatCurrency(property.value)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            property.status === 'Active' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {property.status === 'Active' ? <FiCheckCircle className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
                            {property.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-gray-500 text-sm">
                            <FiCalendar className="w-3 h-3 text-gray-400" />
                            {formatDate(property.startDate)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedProperty(property); }}
                            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiHome className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">No properties found</p>
                          <p className="text-sm text-gray-400 mt-1">Your properties will appear here</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Property Details Modal */}
      <AnimatePresence>
        {selectedProperty && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setSelectedProperty(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FiHome className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-xl">{selectedProperty.title}</h2>
                      <p className="text-indigo-100 text-sm">Receipt #{selectedProperty.receiptNumber}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedProperty(null)} className="text-white/80 hover:text-white hover:bg-white/20 rounded-xl p-2 transition">
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiDollarSign className="w-4 h-4 text-indigo-500" />
                      Property Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Title</span>
                        <span className="font-medium text-gray-800">{selectedProperty.title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Receipt Number</span>
                        <span className="font-mono text-sm text-indigo-600">{selectedProperty.receiptNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Area</span>
                        <span className="font-medium text-gray-800">{selectedProperty.area} {selectedProperty.areaUnit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Location</span>
                        <span className="font-medium text-gray-800">{selectedProperty.location}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiDollarSign className="w-4 h-4 text-indigo-500" />
                      Financial Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Value</span>
                        <span className="font-bold text-indigo-600">{formatCurrency(selectedProperty.value)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Amount in Words</span>
                        <span className="font-medium text-gray-800">{selectedProperty.amountInWords}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Payment Mode</span>
                        <span className="font-medium text-gray-800">{selectedProperty.paymentMode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Payment Details</span>
                        <span className="font-medium text-gray-800">{selectedProperty.paymentDetails}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiUser className="w-4 h-4 text-indigo-500" />
                      Customer Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Name</span>
                        <span className="font-medium text-gray-800">{selectedProperty.customerName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Email</span>
                        <span className="text-gray-700">{selectedProperty.customerEmail}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Phone</span>
                        <span className="text-gray-700">{selectedProperty.customerPhone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiUser className="w-4 h-4 text-indigo-500" />
                      Partner Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Name</span>
                        <span className="font-medium text-gray-800">{selectedProperty.partnerName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Email</span>
                        <span className="text-gray-700">{selectedProperty.partnerEmail}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Phone</span>
                        <span className="text-gray-700">{selectedProperty.partnerPhone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiClock className="w-4 h-4 text-indigo-500" />
                      Dates & Status
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Receipt Date</span>
                        <span className="font-medium text-gray-800">{formatDate(selectedProperty.startDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Created At</span>
                        <span className="font-medium text-gray-800">{formatDate(selectedProperty.createdAt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Updated At</span>
                        <span className="font-medium text-gray-800">{formatDate(selectedProperty.updatedAt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Status</span>
                        <span className={`font-medium ${selectedProperty.status === 'Active' ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {selectedProperty.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setSelectedProperty(null)}
                    className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Properties;